<template>
  <div class="customized-fee-content">
    <Loading :loading="loading" loadingText="Loading...">
    <div class="customized-fee-wrapper" :class="{ 'loading': loading }">
      <!-- Explanation text -->
      <div class="customized-fee-explanation">
        Customized fee explanation
      </div>

      <!-- Customized Fee Table -->
      <div class="customized-fee-table">
        <div class="table-header">
          <div class="th fee-name">FEE NAME & CHARGE REASONS</div>
          <div class="th amount">AMOUNT</div>
          <div class="th actions">ACTIONS</div>
        </div>

        <div class="table-body">
          <!-- No data state -->
          <div v-if="!loading && feeList.length === 0" class="no-data-state">
            <p>No customized fees configured.</p>
          </div>

          <!-- Data rows -->
          <div
            v-for="fee in feeList"
            :key="fee.id"
            class="table-row"
          >
            <!-- Fee Name -->
            <div class="td fee-name">
              <input
                v-if="fee.isEditing"
                v-model="fee.editName"
                type="text"
                class="inline-input"
                :disabled="!isEditable"
                @keyup.enter="confirmEdit(fee)"
              />
              <span v-else>{{ fee.name }}</span>
            </div>

            <!-- Amount -->
            <div class="td amount">
              <div v-if="fee.isEditing" class="amount-input-wrapper">
                <span class="currency-prefix">{{ currencySymbol }}</span>
                <input
                  v-model="fee.editAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  class="inline-input amount-input"
                  :disabled="!isEditable"
                  @keyup.enter="confirmEdit(fee)"
                />
              </div>
              <span v-else>{{ currencySymbol }}{{ fee.amount }}</span>
            </div>

            <!-- Actions -->
            <div class="td actions">
              <!-- Confirm button when editing -->
              <button
                v-if="fee.isEditing"
                class="confirm-btn"
                :disabled="saving"
                @click="confirmEdit(fee)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="#004879" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Confirm</span>
              </button>

              <!-- Edit button when not editing -->
              <button
                v-else
                class="edit-btn"
                :class="{ 'disabled': !isEditable }"
                :disabled="!isEditable"
                @click="startEdit(fee)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M21.4555 5.41575C21.6477 5.70687 21.6157 6.10248 21.3594 6.35876L12.167 15.5511C12.0728 15.6454 11.9552 15.7128 11.8263 15.7465L7.99783 16.7465C7.87296 16.7791 7.74425 16.7784 7.62332 16.7476C7.49475 16.7149 7.37498 16.6482 7.27797 16.5511C7.08969 16.3629 7.01535 16.0889 7.08264 15.8313L8.08264 12.0028C8.11211 11.89 8.1674 11.7786 8.24389 11.6912L17.4703 2.46967C17.5511 2.38891 17.6484 2.32846 17.7543 2.29163C17.8328 2.26432 17.916 2.25 18.0007 2.25C18.1996 2.25 18.3903 2.32902 18.531 2.46967L21.3594 5.2981C21.396 5.33471 21.4281 5.37416 21.4555 5.41575ZM19.7684 5.82843L18.0007 4.06066L9.48251 12.5788L8.85752 14.9716L11.2503 14.3466L19.7684 5.82843Z" :fill="isEditable ? '#374957' : '#C4C4C4'"/>
                  <path d="M19.642 17.1603C19.9154 14.8227 20.0024 12.4688 19.903 10.1208C19.8982 10.0084 19.9405 9.89898 20.0201 9.81942L21.0034 8.83609C21.1243 8.71519 21.3309 8.79194 21.3422 8.96254C21.5271 11.7522 21.457 14.5545 21.1319 17.3346C20.8953 19.3571 19.271 20.9421 17.2589 21.167C13.7924 21.5544 10.209 21.5544 6.74244 21.167C4.73038 20.9421 3.10605 19.3571 2.8695 17.3346C2.45496 13.7903 2.45496 10.2097 2.8695 6.66543C3.10605 4.6429 4.73038 3.05789 6.74244 2.83301C9.37219 2.5391 12.0691 2.46815 14.7313 2.62016C14.9029 2.62996 14.9811 2.83757 14.8596 2.95909L13.867 3.95165C13.7883 4.03034 13.6804 4.07261 13.5692 4.06885C11.3428 3.99376 9.10122 4.07872 6.90905 4.32373C5.57894 4.47239 4.51346 5.522 4.35934 6.83968C3.95834 10.2682 3.95834 13.7318 4.35934 17.1603C4.51346 18.478 5.57894 19.5276 6.90905 19.6763C10.2649 20.0513 13.7365 20.0513 17.0923 19.6763C18.4224 19.5276 19.4879 18.478 19.642 17.1603Z" :fill="isEditable ? '#374957' : '#C4C4C4'"/>
                </svg>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
    </Loading>
  </div>
</template>

<script>
import { getBillingSettingApi, getBillingApplySettingApi, setBillingSettingCustomerApi } from '@/api/setting';
import { notify } from '@/utils/notify';
import Loading from '@/NewLibrary/Loading/Loading.vue';

export default {
  name: 'CustomizedFee',
  components: {
    Loading,
  },

  data() {
    return {
      loading: false,
      saving: false,
      feeList: [],
      billingSettings: null,
      billingApplySettings: null,
    };
  },

  computed: {
    isEditable() {
      const applySettings = this.billingApplySettings?.billing_consultation_fee || {};
      const applyToAll = applySettings.apply_to_all;
      const usePractice = applySettings.use_practice;
      return !applyToAll && !usePractice;
    },

    // Currency symbol from Vuex store (global currency management)
    currencySymbol() {
      return this.$store?.getters['currency/currencySymbol'] || '$';
    },
  },

  mounted() {
    this.fetchCustomizedFeeData();
  },

  methods: {
    async fetchCustomizedFeeData() {
      this.loading = true;
      try {
        // Fetch billing settings and apply settings in parallel
        const [billingRes, applyRes] = await Promise.all([
          getBillingSettingApi(),
          getBillingApplySettingApi()
        ]);

        this.billingSettings = billingRes.data;
        this.billingApplySettings = applyRes.data;

        console.log('Billing settings:', this.billingSettings);
        console.log('Apply settings:', this.billingApplySettings);

        // Determine which value to use (practice or personal)
        const consultationFee = this.getEffectiveConsultationFee();
        
        // Build customized fee list
        this.feeList = [];
        
        if (consultationFee && consultationFee.name) {
          this.feeList.push({
            id: 'consultation_fee',
            name: consultationFee.name || 'Consultation Fee',
            amount: parseFloat(consultationFee.amount || 0).toFixed(2)
          });
        }

        console.log('Customized fee list:', this.feeList);
      } catch (error) {
        console.error('Error fetching customized fee data:', error);
        this.feeList = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Determine which consultation fee value to use based on apply settings
     * Logic:
     * - If apply_to_all is true: use practice value
     * - If apply_to_all is false and use_practice is true: use practice value
     * - If apply_to_all is false and use_practice is false: use personal value
     */
    getEffectiveConsultationFee() {
      if (!this.billingSettings) return null;

      const practice = this.billingSettings.practice || {};
      const personal = this.billingSettings.personal || {};
      const applySettings = this.billingApplySettings?.billing_consultation_fee || {};

      const applyToAll = applySettings.apply_to_all;
      const usePractice = applySettings.use_practice;

      // Determine source based on apply settings
      const usePracticeValue = applyToAll || usePractice;
      const source = usePracticeValue ? practice : personal;

      return {
        amount: source.consultation_fee || '0',
        name: source.consultation_fee_name || 'Consultation Fee'
      };
    },

    startEdit(fee) {
      fee.isEditing = true;
      fee.editName = fee.name;
      fee.editAmount = fee.amount;
    },

    async confirmEdit(fee) {
      if (!this.isEditable) return;

      this.saving = true;
      try {
        const personal = this.billingSettings?.personal || {};
        const applySettings = this.billingApplySettings || {};

        // Build old_ prefixed object from personal data
        const oldPersonal = {};
        Object.keys(personal).forEach(key => {
          oldPersonal['old_' + key] = personal[key];
        });

        // Build apply settings payload
        const applyPayload = {
          apply_billing_consultation_fee: applySettings.billing_consultation_fee?.apply_to_all,
          apply_billing_consultation_fee_use_practice: applySettings.billing_consultation_fee?.use_practice,
          apply_patient_communication_billing: applySettings.patient_communication_billing?.apply_to_all,
          apply_patient_communication_billing_use_practice: applySettings.patient_communication_billing?.use_practice,
          apply_payment_method: applySettings.payment_method?.apply_to_all,
          apply_payment_method_use_practice: applySettings.payment_method?.use_practice,
          apply_payout_method: applySettings.billing_payout_method?.apply_to_all,
          apply_payout_method_use_practice: applySettings.billing_payout_method?.use_practice,
        };

        // Build old_ prefixed apply settings
        const oldApplyPayload = {};
        Object.keys(applyPayload).forEach(key => {
          oldApplyPayload['old_' + key] = applyPayload[key];
        });

        // Merge all: personal data + old personal + apply settings + new edited values
        const payload = {
          ...personal,
          ...oldPersonal,
          ...applyPayload,
          ...oldApplyPayload,
          // Override with new edited values
          consultation_fee_name: fee.editName,
          consultation_fee: String(fee.editAmount),
          old_consultation_fee_name: fee.name,
          old_consultation_fee: String(fee.amount),
        };

        await setBillingSettingCustomerApi(payload);

        fee.name = fee.editName;
        fee.amount = parseFloat(fee.editAmount).toFixed(2);
        fee.isEditing = false;

        if (this.billingSettings?.personal) {
          this.billingSettings.personal.consultation_fee = String(fee.editAmount);
          this.billingSettings.personal.consultation_fee_name = fee.editName;
        }

        this.showAlert('Consultation fee updated successfully');

        this.$emit('fee-updated', fee);
      } catch (error) {
        console.error('Error saving fee:', error);
        this.showAlert('Failed to update fee. Please try again.', 'error');
      } finally {
        this.saving = false;
      }
    },

    showAlert(message, type = 'success') {
      notify({
        type: type,
        title: type === 'success' ? 'Success' : 'Error',
        message: message,
      });
    },

    handleEditFee(fee) {
      console.log('Edit fee:', fee);
      this.$emit('edit-fee', fee);
    },
  },
};
</script>

<style scoped>
.customized-fee-content {
  position: relative;
  padding: 20px 0;
  min-height: 500px;
}

.customized-fee-wrapper {
  opacity: 1;
  transition: opacity 0.2s;
}

.customized-fee-wrapper.loading {
  opacity: 0.5;
  pointer-events: none;
}

.customized-fee-explanation {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: var(--SecondaryBlack, #666);
  margin-bottom: 12px;
  padding: 0;
}

.customized-fee-table {
  border: none;
  border-radius: 5px 5px 0 0;
  overflow: hidden;
  background: transparent;
}

.customized-fee-table .table-header {
  display: flex;
  background: var(--Cool-White, #F9FBFD);
  border-bottom: none;
  border-radius: 5px 5px 0 0;
  padding: 4px 16px;
  gap: 16px;
  align-items: center;
  align-self: stretch;
}

.customized-fee-table .table-header .th {
  padding: 0;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: var(--IconGrey, #374957);
  box-sizing: border-box;
}

.customized-fee-table .table-header .th.fee-name {
  flex: 1 0 0;
}

.customized-fee-table .table-header .th.amount {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1 0 0;
  align-self: stretch;
}

.customized-fee-table .table-header .th.actions {
  width: 100px;
  text-align: center;
  flex-shrink: 0;
}

.customized-fee-table .table-body {
  min-height: 100px;
  background: #fff;
}

.customized-fee-table .table-row {
  display: flex;
  padding: 8px 16px;
  gap: 16px;
  align-items: center;
  align-self: stretch;
  border-bottom: none;
  min-height: 56px;
}

.customized-fee-table .table-row:last-child {
  border-bottom: none;
}


.customized-fee-table .table-row .td {
  padding: 0;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: var(--TrueBlack, #333);
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.customized-fee-table .table-row .td.fee-name {
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 0 0;
}

.customized-fee-table .table-row .td.fee-name span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customized-fee-table .table-row .td.amount {
  flex: 1 0 0;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: var(--SecondaryBlack, #666);
  text-align: center;
}

.customized-fee-table .table-row .td.actions {
  width: 100px;
  justify-content: center;
  flex-shrink: 0;
}

.customized-fee-table .table-row .td.actions .edit-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.customized-fee-table .table-row .td.actions .edit-btn:hover {
  background-color: rgba(55, 73, 87, 0.1);
}

.customized-fee-table .table-row .td.actions .edit-btn.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.customized-fee-table .table-row .td.actions .edit-btn.disabled:hover {
  background-color: transparent;
}

.customized-fee-table .table-row .td.actions .confirm-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: #004879;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.customized-fee-table .table-row .td.actions .confirm-btn:hover {
  background: #003660;
}

.customized-fee-table .table-row .td.actions .confirm-btn svg path {
  stroke: #fff;
}

.customized-fee-table .table-row .td.actions .confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.inline-input {
  width: 100%;
  padding: 8px 12px;
  margin: 0;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #333;
  outline: none;
  background: #fff;
  -moz-appearance: textfield;
  appearance: textfield;
  box-sizing: border-box;
}

.inline-input::-webkit-outer-spin-button,
.inline-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.inline-input:focus {
  border-color: #004879;
}

.inline-input:disabled {
  background-color: #F5F5F5;
  cursor: not-allowed;
}

.amount-input-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.currency-prefix {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #666;
}

.amount-input {
  width: 100px;
  text-align: left;
}

.customized-fee-table .no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #999;
  font-size: 14px;
}

</style>
